import { formatDataValue } from 'utils/formatFns';
import { booleanToYN } from 'utils/general';

const TEAM_MEMBER_LEASE_LABEL = 'Team Member Lease';

export const getNOMValue = ({ TMLI, value, defaultValue }) =>
  TMLI ? value || defaultValue || '' : defaultValue || '';

export const getCCValue = ({ companyCarIndicator, value, defaultValue }) =>
  companyCarIndicator ? value || defaultValue || '' : defaultValue || '';

export const companyCarFields = {
  companyCarStatusCodeDescription: {
    label: 'Company Car Subtype Code | Description',
    getValue: (data) => ({
      statusCode: data?.companyCar?.companyCarStatusCode,
      statusDescription: data?.companyCar?.companyCarStatusDescription,
    }),
    formatValue: ({ statusCode, statusDescription }) => {
      if (statusCode && !statusDescription) {
        return formatDataValue({ value: statusCode });
      }
      if (statusDescription) {
        const formattedCode = formatDataValue({ value: statusCode });
        const formattedDescription = formatDataValue({ value: statusDescription });
        return `${formattedCode} | ${formattedDescription}`;
      }
      return null;
    },
  },
  companyCarWholesaleArea: {
    label: 'Company Car Wholesale Area',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      regionCode: data?.generalInfo?.vehicleAttrs?.sellToRegionCode,
      regionName: data?.generalInfo?.vehicleAttrs?.sellToRegionName,
    }),
    formatValue: (value) => {
      const regionCode = formatDataValue({ value: value.regionCode });
      const regionName = formatDataValue({ value: value.regionName });
      const combinedValue = `${regionCode} | ${regionName}`;

      return getNOMValue({
        TMLI: value.TMLI,
        value: formatDataValue({ value: combinedValue }),
        defaultValue: 'N/A',
      });
    },
  },
  companyCarWholesaleDealer: {
    label: 'Company Car Wholesale Dealer',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      dealerCode: data?.generalInfo?.vehicleAttrs?.sellToDealerCode,
      dealerName: data?.generalInfo?.vehicleAttrs?.sellToDealerName,
    }),
    formatValue: (value) => {
      const dealerCode = formatDataValue({ value: value.dealerCode });
      const dealerName = formatDataValue({ value: value.dealerName });
      const combinedValue = `${dealerCode} | ${dealerName}`;

      return getNOMValue({
        TMLI: value.TMLI,
        value: combinedValue,
        defaultValue: 'N/A',
      });
    },
  },
  costCenter: {
    label: 'Cost Center',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      costCenter: data?.companyCar?.costCenter,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.costCenter }),
      });
    },
  },
  driver: {
    label: 'Driver',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      driver: data?.companyCar?.companyCarDriver,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.driver }),
      });
    },
  },
  inServiceDate: {
    label: 'In Service Date',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      date: data?.companyCar?.inServiceDateFmt,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.date }),
      });
    },
  },
  inServiceDateHero: {
    label: 'In Service Date',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      date: data?.companyCar?.inServiceDateFmt,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.date }),
        defaultValue: 'N/A',
      });
    },
  },
  newOrderManagementShipToArea: {
    label: 'Ship to Area',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      regionCode: data?.generalInfo?.vehicleAttrs?.shipToRegionCode,
      regionName: data?.generalInfo?.vehicleAttrs?.shipToRegionName,
    }),
    formatValue: (value) => {
      const regionCode = formatDataValue({ value: value.regionCode });
      const regionName = formatDataValue({ value: value.regionName });
      const combinedValue = `${regionCode} | ${regionName}`;

      return getNOMValue({
        TMLI: value.TMLI,
        value: combinedValue,
      });
    },
  },
  newOrderManagementWholesaleArea: {
    label: 'Wholesale Area',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      regionCode: data?.generalInfo?.vehicleAttrs?.sellToRegionCode,
      regionName: data?.generalInfo?.vehicleAttrs?.sellToRegionName,
    }),
    formatValue: (value) => {
      const regionCode = formatDataValue({ value: value.regionCode });
      const regionName = formatDataValue({ value: value.regionName });
      const combinedValue = `${regionCode} | ${regionName}`;

      return getNOMValue({
        TMLI: value.TMLI,
        value: combinedValue,
      });
    },
  },
  newOrderManagementWholesaleDealer: {
    label: 'Wholesale Dealer',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      dealerCode: data?.generalInfo?.vehicleAttrs?.sellToDealerCode,
      dealerName: data?.generalInfo?.vehicleAttrs?.sellToDealerName,
    }),
    formatValue: (value) => {
      const dealerCode = formatDataValue({ value: value.dealerCode });
      const dealerName = formatDataValue({ value: value.dealerName });
      const combinedValue = `${dealerCode} | ${dealerName}`;

      return getNOMValue({
        TMLI: value.TMLI,
        value: combinedValue,
      });
    },
  },
  pickupDate: {
    label: 'Pick-Up Date',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      date: data?.companyCar?.tmPickUpDateFmt,
    }),
    formatValue: (value) => {
      return getNOMValue({
        TMLI: value.TMLI,
        value: formatDataValue({ value: value.date }),
      });
    },
  },
  pickupDateHero: {
    label: 'Pick-Up Date',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      date: data?.companyCar?.tmPickUpDateFmt,
    }),
    formatValue: (value) => {
      return getNOMValue({
        TMLI: value.TMLI,
        value: formatDataValue({ value: value.date }),
        defaultValue: 'N/A',
      });
    },
  },
  responsibleDept: {
    label: 'Responsible Dept.',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      costCenter: data?.companyCar?.costCenter,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.costCenter }),
      });
    },
  },
  soldDate: {
    label: 'Sold Date',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      date: data?.companyCar?.companyCarSoldDateFmt,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.date }),
      });
    },
  },
  soldDateHero: {
    label: 'Sold Date',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      date: data?.companyCar?.companyCarSoldDateFmt,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.date }),
        defaultValue: 'N/A',
      });
    },
  },
  subTypeCodeDescription: {
    label: 'Subtype Code | Description',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      subTypeCode: data?.companyCar?.companyCarSubTypeCode,
      subTypeDescription: data?.companyCar?.companyCarSubTypeDescription,
    }),
    formatValue: (value) => {
      const formattedCode = formatDataValue({ value: value.subTypeCode });
      const formattedDescription = formatDataValue({ value: value.subTypeDescription });
      const combinedValue = `${formattedCode} | ${formattedDescription}`;

      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: combinedValue,
      });
    },
  },
  teamMemberLease: {
    label: TEAM_MEMBER_LEASE_LABEL,
    getValue: (data) => data?.companyCar?.teamMemberLeaseIndicator,
    formatValue: (value) => (value ? booleanToYN(value) : null),
  },
  teamMemberLeaseIndicator: {
    label: TEAM_MEMBER_LEASE_LABEL,
    getValue: (data) => data?.companyCar?.teamMemberLeaseIndicator,
    formatValue: (value) => (value ? TEAM_MEMBER_LEASE_LABEL : null),
  },
  tmPickUpDate: {
    label: 'TM Pick Up Date',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      date: data?.companyCar?.tmPickUpDateFmt,
    }),
    formatValue: (value) => {
      return getNOMValue({
        TMLI: value.TMLI,
        value: formatDataValue({ value: value.date }),
      });
    },
  },
  typeCodeDescription: {
    label: 'Type Code | Description',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      typeCode: data?.companyCar?.companyCarTypeCode,
      typeDescription: data?.companyCar?.companyCarTypeDescription,
    }),
    formatValue: (value) => {
      const formattedCode = formatDataValue({ value: value.typeCode });
      const formattedDescription = formatDataValue({ value: value.typeDescription });
      const combinedValue = `${formattedCode} | ${formattedDescription}`;

      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: combinedValue,
      });
    },
  },
  upForSaleDate: {
    label: 'Up for Sale Date',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      date: data?.companyCar?.upForSaleDateFmt,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.date }),
      });
    },
  },
  upForSaleDateHero: {
    label: 'Up for Sale Date',
    getValue: (data) => ({
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      date: data?.companyCar?.upForSaleDateFmt,
    }),
    formatValue: (value) => {
      return getCCValue({
        companyCarIndicator: value.companyCarIndicator,
        value: formatDataValue({ value: value.date }),
        defaultValue: 'N/A',
      });
    },
  },
};
